import { useEffect, useState } from 'react';
import { useOutletContext, Outlet } from 'react-router-dom';
import { useQueryState, IconLoader, Filters, useFilters, Table } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  getCardsTransactions,
  onFilterCardsTransactionsList,
  clearAccountsTransactionState,
  onSortCardsTransactionsList,
} from '../store/slices/statements';
import {
  overviewCardsColumns,
  overviewCardsColumnsLabels,
  overviewCardsFiltersSchema,
  overviewCardsSchema,
} from './variables/statements-cards';
import { OrganizationType, CardsTransactions } from '../types';
import { StatementsContextType } from './types';
import formatCardTransacationList from './helpers/format-card-transaction';

import cn from './styles.module.css';

const StatementsCardsTransaction = ({
  customerType,
  page,
}: {
  customerType: OrganizationType;
  page: 'accounts' | 'aml' | 'overview';
}) => {
  const { cards } = useOutletContext<StatementsContextType>();

  const [cardId, setCardId] = useState('');

  const dispatch = useAppDispatch();

  const { cardsTransactions } = useAppSelector(
    (state) => state.customerStatements,
  );

  const { filters, sort, onFilterChange, onSortChange } =
    useQueryState(overviewCardsSchema);

  const { filtersComponent, handleSubmit, reset } = useFilters({
    filtersSchema: overviewCardsFiltersSchema,
    filters,
    onFilterChange,
  });

  const onFilter = (values: Filters) => {
    const body = { ...values };

    onFilterChange(values);

    if (values.date) {
      body.date.value = values.date.value?.map((item) =>
        new Date(item).toISOString(),
      );
    }

    dispatch(onFilterCardsTransactionsList(body));
  };

  const onSort = (name: string) => {
    onSortChange(name);

    dispatch(
      onSortCardsTransactionsList({
        name,
        direction: sort.sortDir === 'asc' ? 'desc' : 'asc',
      }),
    );
  };

  useEffect(() => {
    reset(filters);
  }, [filters, reset]);

  useEffect(() => {
    if (!cardId && cards && cards.length) {
      setCardId(cards[0]);
    }
  }, [cardId, cards]);

  useEffect(() => {
    dispatch(clearAccountsTransactionState());
  }, [dispatch]);

  useEffect(() => {
    if (cardId && !cardsTransactions.values) {
      dispatch(
        getCardsTransactions({
          name: sort?.sortProp,
          direction: sort?.sortDir as 'asc' | 'desc',
          filters: {
            ...filters,
          },
          cardId,
          customerType,
          page,
        }),
      );
    }
  }, [dispatch, cardsTransactions.values, cardId, sort, filters]);

  let content;

  if (cardsTransactions.isLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={overviewCardsColumns}
        data={
          formatCardTransacationList(
            cardsTransactions.values as CardsTransactions[],
          ) || []
        }
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={overviewCardsColumnsLabels}
        onFilterChange={(values) => onFilter(values)}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={cardsTransactions.requestInProgress}
        tableFormClassName={cn.overviewStatementsTableForm}
        tableHeadCellClassName={cn.overviewStatementsTableHeadCell}
      />
    );
  }

  return (
    <>
      {cards?.length > 1 && (
        <div className={cn.overviewStatementsAccountsWrapper}>
          {cards.map((item) => (
            <button
              key={item}
              type="button"
              className={`${cn.overviewStatementsAccountButton} ${
                cardId === item ? cn.overviewStatementsAccountButtonActive : ''
              }`}
              onClick={() => {
                setCardId(item);
                dispatch(
                  getCardsTransactions({
                    cardId: item,
                    customerType,
                    page,
                  }),
                );
              }}
            >
              {item}
            </button>
          ))}
        </div>
      )}
      <div className={cn.overviewStatementsContentTable}>{content}</div>
      <Outlet />
    </>
  );
};

export default StatementsCardsTransaction;
